import { TimePicker } from "@msys/ui";
import { FormControl, TextFieldProps } from "@mui/material";
import { useField, useFormikContext } from "formik";
import { isString } from "lodash-es";
import moment, { type Moment } from "moment/moment.js";
import { ChangeEvent } from "react";
import { browserHasInputTime } from "../../featureDetection.js";

function toMoment(value: string | null | undefined): Moment | null {
  if (!value) return null;
  const m = moment(value, "HH:mm");
  return m.isValid() ? m : null;
}

function toTimeString(value: Moment | null): string | null {
  if (!value) return null;
  return value.format("HH:mm");
}

export interface Props extends Omit<TextFieldProps, "value" | "onChange"> {
  id?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  name: string;
  label: string;
  value?: string | null;
  placeholder?: string;
  required?: boolean;
  onChange?: (
    e: ChangeEvent<HTMLInputElement> | null,
    newValue: string | null
  ) => void | Promise<void>;
  textFieldProps?: Omit<TextFieldProps, "value" | "defaultValue" | "onChange">;
}

export const TimePickerField = ({
  fullWidth = true,
  disabled,
  label,
  name,
  value: valueProp,
  onChange: onChangeProp,
  placeholder,
  textFieldProps,
  ...otherProps
}: Props) => {
  const [{ value }, { error }, { setValue }] = useField<string | null>(name);
  const { isSubmitting } = useFormikContext<Record<string, any>>();

  const valueMoment = toMoment(valueProp !== undefined ? valueProp : value);
  const onChangeMoment = async (
    e: ChangeEvent<HTMLInputElement> | null,
    newValue: Moment | null
  ) => {
    const newValueString = toTimeString(newValue);
    if (onChangeProp) {
      await onChangeProp(e, newValueString);
    } else {
      await setValue(newValueString);
    }
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      required={otherProps.required}
      error={Boolean(error)}
      disabled={disabled ?? isSubmitting}
    >
      <TimePicker
        browserHasInputTime={browserHasInputTime}
        label={label}
        disabled={disabled ?? isSubmitting}
        value={valueMoment}
        onChange={onChangeMoment}
        {...otherProps}
        textFieldProps={{
          name,
          placeholder,
          error: Boolean(error),
          disabled: disabled ?? isSubmitting,
          slotProps: {
            inputLabel: {
              shrink: true,
            },
            input: {
              inputProps: {
                step: 60, // 1 min
              },
            },
          },
          helperText: Boolean(error) && isString(error) ? error : undefined,
          required: otherProps.required,
          ...textFieldProps,
        }}
      />
    </FormControl>
  );
};
